import { useMemo } from 'react';

import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useAppSelector } from '@redux/hooks';
import { selectCustomerData, selectIsSavingEstimate } from '@redux/slices/estimateSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '../../axiosBaseQuery';
import { CfiCompany, CfiCompanyQueryParams, useFetchCfisQuery } from './cfisService';

type useCfisReturn = TypedUseQueryHookResult<CfiCompany[], CfiCompanyQueryParams, AxiosBaseQueryFunction> & {
    cfi?: CfiCompany;
};

export const useCfis = (): useCfisReturn => {
    const customerData = useAppSelector(selectCustomerData);
    const zipCode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();
    const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
    const { fetchedEstimate } = useFetchedEstimate();
    const { isFetchingDependencies } = useFetchDependencies();

    /**********************
     Commit dacd0439203866aa69cff25f94eac111a756862a introduced the three commented out useMemo address values, but it looks like they
     were never fully implemented, so I'm commenting them out for now.
     *********************/
    // EZE-1541 - this is only used by enteredAddress, so commenting it out as well
    // const fetchedAddress = useMemo(
    //     () =>
    //         fetchedEstimate
    //             ? {
    //                   address: fetchedEstimate.customerAddress1,
    //                   city: fetchedEstimate.customerCity,
    //                   state: fetchedEstimate.customerState,
    //               }
    //             : {},
    //     [fetchedEstimate]
    // );

    // EZE-1541 - this is only used by installAddress, so commenting it out as well
    // const enteredAddress = useMemo(
    //     () =>
    //         customerData ? { address: customerData.address1, city: customerData.city, state: customerData.state } : {},
    //     [customerData]
    // );

    // if there's a fetchedAddress, start with that, if we're in the process of saving and there's customerData, use that
    // EZE-1541 - this was giving linting errors and isn't currently being used, it looks like it was never fully implemented (see TODO below)
    // const installAddress = useMemo(
    //     () => ({
    //         ...fetchedAddress,
    //         ...(isSavingEstimate && customerData ? enteredAddress : {}),
    //     }),
    //     [customerData, enteredAddress, fetchedAddress, isSavingEstimate]
    // );

    const bestZipCode = useMemo(() => {
        if (isSavingEstimate && customerData?.zip && customerData.zip.length >= 5) {
            return customerData?.zip;
        }
        return fetchedEstimate?.customerZip ?? zipCode;
    }, [customerData?.zip, fetchedEstimate?.customerZip, isSavingEstimate, zipCode]);

    const { cfi, ...rest } = useFetchCfisQuery(
        !isFetchingDependencies && zipCode && selectedStore?.companyId && selectedColor?.qteGrpID
            ? {
                  params: {
                      zipCode: bestZipCode,
                      storeId: selectedStore.companyId,
                      prodQteGrpID: selectedColor.qteGrpID,
                      // ...installAddress, // TODO: figure out what to do with errors, skip the exact lookup for now
                  },
              }
            : skipToken,
        {
            selectFromResult: (result) => ({
                cfi: result.isError ? undefined : result.data?.[0],
                ...result,
            }),
        }
    );
    return { cfi, ...rest };
};
